import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { SvgIconModule } from '../svg-icon/svg-icon.module';

@Component({
  selector: 'twaice-fe-file-dropzone',
  standalone: true,
  imports: [CommonModule, NzEmptyModule, SvgIconModule],
  templateUrl: './file-dropzone.component.html',
  styleUrl: './file-dropzone.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDropzoneComponent {
  @Input() accept = '';
  @Input() disabled = false;
  @Input() isUploading = false;
  @Input() validationError: string | null = null;
  @Input() title = 'Upload file';
  @Input() uploadingTitle = 'Uploading file...';
  @Input() description = 'Drag file here or browse your files';
  @Input() placeholderIconSrc: string | null = null;
  @Input() placeholderIconAlt = '';
  /** Accepts and reports more than one file at once, via `filesSelected` instead of
   * `fileSelected` — existing single-file consumers are unaffected since they leave this false. */
  @Input() multiple = false;

  @Output() fileSelected = new EventEmitter<File>();
  @Output() filesSelected = new EventEmitter<File[]>();

  @ViewChild('fileInput', { static: true }) private fileInputRef!: ElementRef<HTMLInputElement>;

  isDragging = false;

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    // `input.files` is a live FileList tied to the input's current selection — it must be copied
    // to a plain array before clearing `.value`, since clearing empties that same live list too.
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    this.emitFiles(files);
  }

  openFilePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled && !this.isUploading) {
      this.fileInputRef.nativeElement.click();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled && !this.isUploading) {
      this.isDragging = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    if (this.disabled || this.isUploading) {
      return;
    }

    this.emitFiles(event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : []);
  }

  private emitFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }

    if (this.multiple) {
      this.filesSelected.emit(files);
      return;
    }

    this.fileSelected.emit(files[0]);
  }
}
